// Cognito tokens are stored in cookies (set by /api/auth/callback)

/**
 * Cognito groups for the signed-in user.
 *
 * Read from the `cognito_groups` cookie the /api/auth/* routes write, NOT by
 * decoding the access token — that cookie is httpOnly now, so script cannot see
 * it, which is exactly what stops an XSS from stealing the session.
 *
 * UI hint only. This cookie is script-writable, so nothing here is an
 * authorization decision: the backend re-derives groups from the verified token
 * on every request. Faking it gets you a different-looking menu and a 403.
 */
const getGroups = (): string[] => {
  if (typeof window === 'undefined') return []
  const match = document.cookie.match(/(?:^|; )cognito_groups=([^;]*)/)
  if (!match) return []
  try {
    const parsed = JSON.parse(decodeURIComponent(match[1]))
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/**
 * Is there a session? `auth_session_start` is set next to the access token on
 * every login path and is readable by design, so it stands in for the token's
 * presence now that the token itself is invisible to script.
 */
export const isAuthenticated = (): boolean => {
  if (typeof window === 'undefined') return false
  return document.cookie.includes('auth_session_start=')
}

// Single entry point for navigating to /api/auth/logout. Both the explicit
// Logout button (logout(), below) and the axios interceptor's own
// failed-refresh handler (lib/api.ts) used to fire window.location.href
// independently, with no guard shared between them. Clicking Logout while
// any background poll/query was in flight (there are several — VP-task
// polling, social-journey polling, React Query's own refetches) meant that
// request's 401 could trigger the interceptor's OWN redirect a moment
// after the button's, and two competing top-level navigations racing
// through Cognito's cross-origin hosted logout domain could leave the
// browser stuck mid-navigation — confirmed live via a DevTools trace
// showing /signout pending forever alongside a 401 on /api/auth/refresh.
// Whichever caller reaches this first wins; every later call this
// page-load is a no-op.
let redirectingToLogout = false
export function redirectToLogout(): void {
  if (redirectingToLogout) return
  redirectingToLogout = true
  window.location.href = '/api/auth/logout'
}

export const logout = (): void => {
  // Clear cookies first (client-side)
  clearAllCognitoCookies()

  // Redirect to logout endpoint which will clear server-side cache and redirect to login
  redirectToLogout()
}

export const getCurrentUser = (): any => {
  if (typeof window === 'undefined') return null
  const match = document.cookie.match(/(?:^|; )cognito_user=([^;]*)/)
  if (!match) return null
  try {
    return JSON.parse(decodeURIComponent(match[1]))
  } catch {
    return null
  }
}

export const isAdmin = (): boolean => getGroups().includes('Administrators')

// Clear all Cognito-related cookies (useful for clearing obsolete tokens)
export const clearAllCognitoCookies = (): void => {
  if (typeof window === 'undefined') return
  
  const cookiesToDelete = [
    'cognito_access_token',
    'cognito_groups',
    'cognito_id_token',
    'cognito_refresh_token',
    'cognito_user',
    'cognito_user_plan',
    'cognito_app_subscription',
    'cognito_selected_company',
    'cached_companies',
    'auth_session_start',
    'oidc_nonce',
    'oidc_state',
    'redirect_after_login',
  ]
  
  const hostname = window.location.hostname
  const baseDomain = hostname.split('.').slice(-2).join('.')
  
  cookiesToDelete.forEach(cookieName => {
    // Delete with different path and domain options to ensure it's cleared
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${hostname};`
    if (baseDomain !== hostname) {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${baseDomain};`
    }
  })
  
  // Also clear any localStorage items that might be related
  try {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('cognito_access_token')
    localStorage.removeItem('cognito_id_token')
    localStorage.removeItem('cognito_user')
  } catch (e) {
    // Ignore localStorage errors (e.g., in private browsing mode)
  }

  // Clear sessionStorage as well
  try {
    sessionStorage.clear()
  } catch (e) {
    // Ignore sessionStorage errors
  }
}

/**
 * Returns the user's plan level.
 * Reads the cognito_user_plan cookie set by /api/auth/callback and /api/auth/refresh.
 * Defaults to 'legend' if cookie absent or unrecognised value.
 */
export const getUserPlan = (): 'free' | 'pro' | 'legend' => {
  if (typeof window === 'undefined') return 'legend'
  const match = document.cookie.match(/(?:^|; )cognito_user_plan=([^;]*)/)
  if (!match) return 'legend'
  const plan = decodeURIComponent(match[1])
  if (plan === 'free') return 'free'
  if (plan === 'pro') return 'pro'
  return 'legend' // enterprise or empty
}

/**
 * Returns the user's app subscription.
 * Reads the cognito_app_subscription cookie set by /api/auth/callback and /api/auth/refresh.
 * Defaults to 'marketing_app' if cookie absent or unrecognised value.
 */
export const getAppSubscription = (): 'marketing_app' | 'small_businesses' => {
  if (typeof window === 'undefined') return 'marketing_app'
  const match = document.cookie.match(/(?:^|; )cognito_app_subscription=([^;]*)/)
  if (!match) return 'marketing_app'
  const val = decodeURIComponent(match[1])
  if (val === 'small_businesses') return 'small_businesses'
  return 'marketing_app'
}

/**
 * Client-side guard: ensures user is on the correct page for their app_subscription.
 * If cognito_app_subscription cookie is missing, calls /api/auth/refresh to populate it.
 * Then redirects if user is on the wrong page.
 */
export async function ensureSubscriptionRedirect(expectedSub: 'marketing_app' | 'small_businesses'): Promise<void> {
  if (typeof window === 'undefined') return
  if (!isAuthenticated()) return

  let sub = getAppSubscription()

  // If cookie missing, refresh to populate it from Cognito
  const hasCookie = document.cookie.includes('cognito_app_subscription=')
  if (!hasCookie) {
    try {
      await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' })
      sub = getAppSubscription()
    } catch {
      // refresh failed, use default
    }
  }

  if (sub !== expectedSub) {
    const target = sub === 'small_businesses' ? '/app' : '/'
    window.location.href = target
  }
}

/** Returns company names from Cognito groups (excludes 'Administrators'). */
export const getUserCompanyGroups = (): string[] =>
  getGroups().filter((g) => g !== 'Administrators')

/** Reads the persisted company selection from cookie. */
export const getSelectedCompany = (): string | null => {
  if (typeof window === 'undefined') return null
  const match = document.cookie.match(/(?:^|; )cognito_selected_company=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : null
}

/** Persists the selected company in a 30-day cookie. */
export const saveSelectedCompany = (company: string): void => {
  if (typeof window === 'undefined') return
  document.cookie = `cognito_selected_company=${encodeURIComponent(company)}; max-age=${60 * 60 * 24 * 30}; path=/; SameSite=Lax`
}

/** Reads the cached companies list from cookie. */
export const getCachedCompanies = (): string[] | null => {
  if (typeof window === 'undefined') return null
  const match = document.cookie.match(/(?:^|; )cached_companies=([^;]*)/)
  if (!match) return null
  try {
    const parsed = JSON.parse(decodeURIComponent(match[1]))
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null
  } catch {
    return null
  }
}

/** Persists the companies list in a 7-day cookie. */
export const saveCachedCompanies = (companies: string[]): void => {
  if (typeof window === 'undefined') return
  document.cookie = `cached_companies=${encodeURIComponent(JSON.stringify(companies))}; max-age=${60 * 60 * 24 * 7}; path=/; SameSite=Lax`
}

